import { storeToRefs } from 'pinia';

import {
  PatchedUnifiedProductTemplateRequest,
  ProductApiFactory,
  type ProductApiProductsListRequest,
  ProductCategoryApiFactory,
  ProductCategoryApiProductCategoriesListRequest,
  ProductDosageUnitApiFactory,
  ProductDosageUnitApiProductDosageUnitsListRequest,
  ProductGrowOperationTypeApiFactory,
  ProductImageApiFactory,
  ProductImageApiProductImagesBulkCreateCreateRequest,
  type ProductImageApiProductImagesCloneCreateRequest,
  ProductImageApiProductImagesCreateRequest,
  ProductImageApiProductImagesDestroyRequest,
  ProductImageApiProductImagesListRequest,
  ProductLineApiFactory,
  ProductLineApiProductLinesCreateRequest,
  ProductLineApiProductLinesDestroyRequest,
  ProductLineApiProductLinesListRequest,
  ProductLineApiProductLinesPartialUpdateRequest,
  ProductListingStateApiFactory,
  ProductMVPApiFactory,
  ProductMVPApiProductsMvpAddFromTemplateCreateRequest,
  ProductMVPApiProductsMvpCloneCreateRequest,
  ProductMVPApiProductsMvpCloneRetrieveRequest,
  ProductMVPApiProductsMvpCreateRequest,
  ProductMVPApiProductsMvpPartialUpdateRequest,
  ProductMVPApiProductsMvpRetrieveRequest,
  ProductSpecificationApiFactory,
  ProductSpecificationApiProductSpecificationsListRequest,
  ProductSubcategoryApiFactory,
  ProductSubcategoryApiProductSubcategoriesListRequest,
  ProductTemplateApiFactory,
  ProductTemplateApiProductTemplatesListRequest,
  ProductTemplateApiProductTemplatesRetrieveRequest,
  StrainApiFactory,
  StrainApiStrainsListRequest,
  UnifiedProductBulkUpdateRequest,
  UnifiedProductTemplateRequest,
  UnitDenominationApiFactory,
  UnitDenominationApiUnitDenominationsListRequest,
  UnitOfMeasureApiFactory,
  UnitOfMeasureApiUnitsOfMeasureListRequest,
} from '@/clients/unifiedApi';
import { ServerListViewChangeData } from '@/components/ServerListView/ServerListViewChangeData';
import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { Company, PromoCode } from '@/models/company';
import { Customer } from '@/models/customer';
import { NewCustomMenu, UpdatedCustomMenuItem } from '@/models/customMenus';
import {
  Batch,
  BatchDocument,
  InventoryFacility,
  InventoryItem,
  InventoryProduct,
  ProductBatch,
  ProductInventory,
  Strain,
  StrainClassification,
  WMSInventoryItem,
} from '@/models/inventory';
import { Listing, ListingState, Product, ProductLine, ProductOrder } from '@/models/product';
import { useCompanyStore } from '@/piniaStores/company/company.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import { CustomMenu, CustomMenuDetail } from '@/services/api/models/customMenus';
import { formatBatchInventoryItemOptions, formatFacilities, formatProductsParams } from '@/services/parsers/inventory';
import session, { apiSlugSession, createSession } from '@/services/session';
import buildPath from '@/utils/buildPath';
import JSONTransform from '@/utils/json-transform';

import { getUnifiedApi } from './unifiedApi';
import { fetchInParallelChunks } from './utils';

/**
 * Get Products from API
 * @param params - Any parameters for the endpoint
 * @param params.id - product id(s)
 * @param params.isBrandOwner - optional flag for BME
 * @param params.orderByAvailable - optional param to sort alphabetically and move unavailable to the bottom
 * @returns products
 */
export async function getProducts<ProductType extends object = Product>(
  params: {
    id?: number | number[];
    isBrandOwner?: boolean;
    orderByAvailable?: boolean;
    [key: string]: unknown;
  },
  options?: { requestId?: string },
) {
  const { isBrandOwner, ...queryParams } = params;
  const getParams = {
    ...session.defaults.params,
    include_children: 'images',
    limit: 9999,
    fields_include: [
      'allow_fractional_quantities',
      'brand',
      'display_name',
      'name',
      'sku',
      'quantity',
      'reserved_qty',
      'wholesale_price',
      'sale_price',
      'AVAILABLE_FOR_SAMPLES',
      'unit_of_measure',
      'unit_of_measure_abbreviation',
      'unit_multiplier',
      'unit_denomination',
      'product_line',
      'available_inventory',
      'parent',
      'children_count',
      'extern_sts_ids',
      'category',
      'retail_price',
      'price_schedule_price',
      'license',
      'created_on',
      'inventory_management',
      'listing_state',
      'is_ancillary',
      isBrandOwner ? '' : 'inv_count',
      isBrandOwner ? '' : 'fulfilled_packages_labels',
      isBrandOwner ? '' : 'featured_image',
      isBrandOwner ? '' : 'sts_conversion_amount',
      isBrandOwner ? '' : 'product_line_name',
    ].join(','),
    ...queryParams,
    ...(isBrandOwner && { company_slug: '' }), // Company/index.vue adds a company_slug query parameter, so this line is removing it
  };
  const url = isBrandOwner ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.PRODUCTS}/` : `${URLS.INVENTORY.LIST}/`;

  const data = await fetchInParallelChunks<ProductType>({
    url,
    params: getParams,
    isCompanySpecific: isBrandOwner,
    arrayFormat: 'comma',
    options,
  });

  return data?.results || [];
}

export async function getProductCount(params: { [key: string]: unknown } = {}): Promise<number> {
  const response = await session.get<PaginatedResponse<Product>>(`${URLS.INVENTORY.LIST}/`, {
    params: { ...params, offset: 0, limit: 1 },
  });

  return response.data.count;
}

/**
 * Get products located in a specific facility
 * @param params - any params for the product endpoint
 * @param params.facilityId - id of facility to query products
 * @returns products
 */
export const getProductsInFacility = async (params: {
  limit?: number;
  isBrandOwner?: boolean;
  facilityId: InventoryFacility['id'];
  [key: string]: unknown;
  // eslint-disable-next-line require-await
}) => {
  const { limit = 9999, isBrandOwner, facilityId, ...queryParams } = params;

  const url = isBrandOwner
    ? `${URLS.INVENTORY.FACILITIES}/${facilityId}/${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.PRODUCTS}/`
    : `${URLS.INVENTORY.FACILITIES}/${facilityId}/${URLS.INVENTORY.LIST}/`;

  const data = await fetchInParallelChunks<ProductInventory>({
    url,
    params: { ...queryParams, limit },
    isCompanySpecific: true,
    arrayFormat: 'comma',
  });

  return data.results;
};

/**
 * Get products for active products and listings
 * @param payload
 * @param payload.endpoint - Specific endpoint to hit
 * @param payload.params - Serialized GET params from ServerListItem (or "from api-driven LLListView")
 * @param payload.serialized - Serialized query string
 * @returns paginated products
 */
type ProductsPaginatedUrls = Pick<typeof URLS.INVENTORY, 'ACTIVE_PRODUCTS' | 'INVENTORY_MANAGEMENT' | 'LIST'> &
  Pick<typeof URLS.LISTINGS, 'PRODUCTS'>;
type ProductsPaginatedEndpoints = ProductsPaginatedUrls[keyof ProductsPaginatedUrls];

export interface GetProductsPaginatedParams {
  endpoint: ProductsPaginatedEndpoints;
  fetchAllProducts?: boolean;
  nonListViewSerializedParams?: string;
  params?: ServerListViewChangeData['params'];
  serialized: ServerListViewChangeData['serialized'];
  isCompanySpecific?: boolean;
  trailingSlashEndpoint?: boolean;
}

export const getProductsPaginated = <T extends object>({
  endpoint,
  isCompanySpecific,
  nonListViewSerializedParams = '',
  serialized = '',
  trailingSlashEndpoint,
}: GetProductsPaginatedParams): Promise<PaginatedResponse<T>> => {
  const { activeCompany } = storeToRefs(useCompanyStore());
  const formattedParams = formatProductsParams({
    excludeList: [],
    serialized: `${serialized}&company_slug=${activeCompany.value?.slug}${
      nonListViewSerializedParams && `&${nonListViewSerializedParams}`
    }`,
  });

  return fetchInParallelChunks<T>({
    url: trailingSlashEndpoint ? `${endpoint}/` : endpoint,
    params: formattedParams,
    isCompanySpecific,
    arrayFormat: 'comma',
  });
};

export async function getWmsProductInventory(productIds: Product['id'][]) {
  const queryString = `?product_ids=${productIds.join(',')}&${URLS.CUSTOM_ERROR}`;
  const url = `${URLS.INVENTORY.WMS_PRODUCT_INVENTORY}/${queryString}`;
  const response = await apiSlugSession.get<WMSInventoryItem[]>(url);

  return response.data;
}

/**
 * Get Product Varieties from API
 * @param ids - product ids
 * @returns product varieties
 */
export const getVarieties = async (ids: Product['id'][]) => {
  const response = await session.get<PaginatedResponse<Product>>(`${URLS.INVENTORY.LIST}/?${URLS.CUSTOM_ERROR}`, {
    params: {
      parent: ids.join(','),
      limit: 5000,
    },
  });

  return response.data.results;
};

/**
 * Get Product Categories from the unified API
 */
export const getCategories = async (params: Partial<ProductCategoryApiProductCategoriesListRequest> = {}) => {
  const api = await getUnifiedApi(ProductCategoryApiFactory);

  const response = await api.productCategoriesList(params);

  return response.data.results || [];
};

/**
 * Get a Product Category from the unified API by ID
 */
export const getCategory = async (id: number) => {
  const api = await getUnifiedApi(ProductCategoryApiFactory);

  const response = await api.productCategoriesRetrieve({ id });

  return response.data;
};

/**
 * Get Product SubCategories from the unified API
 */
export const getSubCategories = async (params: ProductSubcategoryApiProductSubcategoriesListRequest = {}) => {
  const api = await getUnifiedApi(ProductSubcategoryApiFactory);
  const response = await api.productSubcategoriesList(params);

  return response.data;
};

/**
 * Get Product Category by ID
 */
export const getSubCategory = async (id: number) => {
  const api = await getUnifiedApi(ProductSubcategoryApiFactory);
  const response = await api.productSubcategoriesRetrieve({ id });

  return response.data;
};

/**
 * Patch product with new properties
 * @param data - product to patch
 * @param data.id - required product id
 */
export const updateProduct = async (data: Partial<Product> & { id: Product['id'] }) => {
  const path = buildPath(URLS.INVENTORY.PRODUCT, { productId: data.id });
  const url = `${path}/?${URLS.CUSTOM_ERROR}`;
  const response = await session.patch<Partial<Product>>(url, data);

  return response.data;
};

/**
 * Update product data
 */
export const updateProductMVP = async (data: ProductMVPApiProductsMvpPartialUpdateRequest) => {
  const api = await getUnifiedApi(ProductMVPApiFactory);
  const response = await api.productsMvpPartialUpdate(data);

  return response.data;
};

interface UpdateBatchPayload {
  id: number;
  batch: number;
  hideBatch: boolean;
}

/**
 * Update Batch associated to line item
 */
export const updateBatchFromOrder = async ({ id, ...data }: UpdateBatchPayload) => {
  const url = `${URLS.ORDERS.LINE_ITEMS}/${id}/`;
  const response = await session.patch(url, data);

  return response.data;
};

interface BatchPayload {
  archived?: boolean;
  productId: number;
  limit?: number;
}

/**
 * Get all batches for a product
 */
export const getBatches = async ({ archived = false, productId, limit = 1000 }: BatchPayload) => {
  const params = { archived, products: productId, limit };
  const url = `${URLS.INVENTORY.BATCHES}/`;
  const response = await session.get(url, { params });

  return response.data.results;
};

export interface PatchActiveProductPayload extends Partial<InventoryProduct> {
  id: InventoryProduct['id'];
}

/**
 * Patch product in the active products endpoint
 * @param data - product to patch
 */
export const patchActiveProduct = async (data: PatchActiveProductPayload) => {
  const path = buildPath(URLS.INVENTORY.ACTIVE_PRODUCT, { productId: data.id });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;
  const params = { archived: false };
  const response = await session.patch<InventoryProduct>(url, data, { params });

  return response.data;
};

/**
 * Fetches a list of Product Lines.
 * @param data
 * @param data.isBrandOwner - optional param to get BME product lines
 * @returns product lines array
 */
export const getProductLines = async (data = { isBrandOwner: false }) => {
  const userStore = useUserStore();

  const { isBrandOwner } = data;
  const endpoint = isBrandOwner
    ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.PRODUCT_LINES}`
    : URLS.INVENTORY.PRODUCT_LINES;
  const params = {
    company_slug: isBrandOwner ? '' : userStore.userActiveCompany?.slug,
    limit: 999,
  };

  const currentSession = isBrandOwner ? apiSlugSession : session;

  const response = await currentSession.get<PaginatedResponse<ProductLine>>(`${endpoint}/?${URLS.CUSTOM_ERROR}`, {
    params,
  });

  return response.data.results;
};

/**
 * Fetches a list of Product listing states.
 * @returns listing states
 */
export const getListingStates = async () => {
  const response = await session.get<ListingState[]>(`${URLS.INVENTORY.LISTING_STATES}/?${URLS.CUSTOM_ERROR}`);

  return response.data;
};

/**
 * Fetches a Custom Menus from API.
 * @returns custom menu list
 */
export const getCustomMenuList = async () => {
  const response = await session.get<PaginatedResponse<CustomMenu>>(
    `${URLS.INVENTORY.CUSTOM_MENU_LIST}/?${URLS.CUSTOM_ERROR}`,
    {
      params: {
        limit: 999,
      },
    },
  );

  return response.data;
};

/**
 * Fetches a single Custom Menu
 * @param id - custom menu id to fetch
 * @returns custom menu
 */
export const getCustomMenuById = async (id: CustomMenuDetail['id']) => {
  const url = `${URLS.INVENTORY.CUSTOM_MENU_LIST}/${id}/?${URLS.CUSTOM_ERROR}`;

  const response = await session.get<CustomMenuDetail>(url, {
    params: {
      limit: 999,
    },
  });

  return response.data;
};

/**
 * Creates a new Custom Menu
 * @param customMenu - menu to be added
 * @returns newly created custom menu
 */
export const createCustomMenu = async (customMenu: NewCustomMenu) => {
  const url = URLS.INVENTORY.CUSTOM_MENU_LIST + '/';
  const body = {
    active: customMenu.active,
    name: customMenu.name,
    owner: customMenu.owner,
    description: customMenu.description,
  };
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();

  const response = await session.post<CustomMenu>(url, body, { params });

  return response.data;
};

/**
 * Removes a Custom Menu using the schedule id
 * @param scheduleId - custom menu id to remove
 */
// eslint-disable-next-line require-await
export const removeCustomMenu = async (scheduleId: CustomMenu['id']) => {
  return session.delete(`${URLS.INVENTORY.CUSTOM_MENU_LIST}/${scheduleId}/?${URLS.CUSTOM_ERROR}`); // CHECK RESPONSE
};

/**
 * Update a Custom Menu's meta data using the schedule id
 */
export const updateCustomMenu = async (customMenu: CustomMenu) => {
  const response = await session.patch<CustomMenu>(
    `${URLS.INVENTORY.CUSTOM_MENU_LIST}/${customMenu.id}/?${URLS.CUSTOM_ERROR}`,
    customMenu,
  );

  return response.data;
};

/**
 * Add products to custom menu items
 * @param products - products to add to custom menu
 * @returns custom menu product
 */
export const addCustomMenuProducts = async (customMenuItems: UpdatedCustomMenuItem[]) => {
  const formattedItems = customMenuItems.map((item) => {
    const newItem = {
      ...item,
      product: item.product,
    };

    delete newItem.id;

    return newItem;
  });

  const response = await session.post(
    `${URLS.INVENTORY.CUSTOM_MENU_ITEMS}/?bulk=true&${URLS.CUSTOM_ERROR}`,
    formattedItems,
  );

  return response.data.response;
};

/**
 * Remove a product from custom menu items
 * @param product - product to remove from custom menu
 */
// eslint-disable-next-line require-await
export const removeCustomMenuProduct = async (customMenuItem: UpdatedCustomMenuItem) => {
  return session.delete(`${URLS.INVENTORY.CUSTOM_MENU_ITEMS}/${customMenuItem.id}/?${URLS.CUSTOM_ERROR}`); // CHECK WHAT DELETE DOES
};

interface UpdateCustomMenuItemWithoutPriceSchedule extends Omit<UpdatedCustomMenuItem, 'product' | 'priceSchedule'> {
  priceSchedule?: number;
}

/**
 * Update price of products in custom menu items
 * @param products - products to update
 * @returns custom menu products
 */
export const updateCustomMenuProducts = async (customMenuItems: UpdateCustomMenuItemWithoutPriceSchedule[]) => {
  const formattedItems = customMenuItems.map((item) => {
    const newItem = {
      ...item,
    };

    delete newItem.priceSchedule;

    return newItem;
  });

  const response = await session.patch(
    `${URLS.INVENTORY.CUSTOM_MENU_ITEMS}/?bulk=true&${URLS.CUSTOM_ERROR}`,
    formattedItems,
  );

  return response.data.response;
};

/**
 * Fetches all PromoCodes for the current Company.
 * @returns promo codes
 */
export const getPromoCodes = async () => {
  const response = await session.get<PaginatedResponse<PromoCode>>(
    `${URLS.INVENTORY.PROMO_CODES}/?${URLS.CUSTOM_ERROR}`,
  );

  return response.data.results;
};

/**
 * Adds a new Promo code.
 * @param data - promo code containing all relevant data
 * @returns created promo code
 */
export const addPromo = async (data: PromoCode) => {
  const response = await session.post<PromoCode>(`${URLS.INVENTORY.PROMO_CODES}/?${URLS.CUSTOM_ERROR}`, data);

  return response.data;
};

/**
 * Updates a Promo code.
 * @param data - the Promo Code to update.
 * @returns updated promo code
 */
export const updatePromo = async (data: PromoCode) => {
  const response = await session.patch<PromoCode>(
    `${URLS.INVENTORY.PROMO_CODES}/${data.id}/?${URLS.CUSTOM_ERROR}`,
    data,
  );

  return response.data;
};

/**
 * Removes a Promo code.
 * @param data - the Promo Code to delete.
 */
export const removePromo = async (data: PromoCode) => {
  const response = await session.delete(`${URLS.INVENTORY.PROMO_CODES}/${data.id}/?${URLS.CUSTOM_ERROR}`); // CHECK RESPONSE

  return response.data;
};

/**
 * Get Product S2S Details from API
 * @param payload
 * @param payload.productId - id of product to query for
 * @param payload.params - any parameters for the endpoint
 * @returns product with s2s available inventory & s2s units of measure fields
 */
export const getProductsStsInventory = async (payload: {
  productId: Product['id'];
  params: { [key: string]: unknown };
}) => {
  const { productId, params } = payload;
  const URL = productId ? `${URLS.INVENTORY.LIST}/${productId}` : URLS.INVENTORY.LIST;

  const response = await session.get<PaginatedResponse<Product>>(`${URL}/?${URLS.CUSTOM_ERROR}`, {
    params: {
      limit: 9999,
      fields_include: ['sts_available_inventory', 'sts_units_of_measures'].join(','),
      ...params,
    },
  });

  if (response.data.results) {
    return response.data.results;
  }

  return response.data;
};

/**
 * Get Products from API
 * @param payload
 * @param payload.productId - id of product to query for
 * @param payload.params - any parameters for the endpoint
 * @returns products with associated orders
 */
export const getProductOrders = async ({
  params,
  productId,
}: {
  params: { [key: string]: unknown };
  productId: Product['id'];
}) => {
  const response = await session.get<ProductOrder>(`${URLS.INVENTORY.LIST}/${productId}/?${URLS.CUSTOM_ERROR}`, {
    params: {
      ...session.defaults.params,
      ...params,
    },
  });

  return response.data;
};

/**
 * Patches product list to API
 * @param products - products to patch
 * @returns patched custom menu products
 */
export const bulkPatchProducts = async (products: { sku: string; quantity: number }[]) => {
  const response = await session.patch<Product[]>(
    `${URLS.INVENTORY.LIST}/${URLS.INVENTORY.BULK_UPDATE}/?${URLS.CUSTOM_ERROR}`,
    products,
  );

  return response.data;
};

/**
 * Get list of companies eligible to add choosen product to the cart
 * @param productId - The product ID to fetch companies of
 * @returns companies able to purchase product
 */
export const getEligibleCompanies = async (productId: Product['id']) => {
  const response = await session.get<Company[]>(
    `${URLS.INVENTORY.DEFAULT}/${productId}/${URLS.INVENTORY.ELIGIBLE_COMPANIES}/?${URLS.CUSTOM_ERROR}`,
  );

  return response.data;
};

/**
 * Saves an array of companyIds (buyerIds) for a specific product
 * @param listings - contains the productId and buyerIds to save
 * @returns updated listings
 */
// eslint-disable-next-line require-await
export const updateListings = async (listings: Listing) => {
  return apiSlugSession.put<Listing>(`${URLS.LISTINGS.PRODUCTS_ASSIGNED}/`, listings);
};

/**
 * Get list of batches associated to a product
 * by default it will return only the active batches
 * @param payload
 * @param payload.productId - id of the product to fetch batches
 * @param payload.includeDeletedBatches - optional flag when request requires both active and inactive batches
 * @returns product batches
 */
export const getProductBatches = async (payload: { productId: Product['id']; includeDeletedBatches?: boolean }) => {
  const { productId, includeDeletedBatches } = payload;

  const params = includeDeletedBatches ? '?include_deleted_batches=true' : '';
  const path = buildPath(URLS.INVENTORY.PRODUCT_BATCHES, { productId });
  const url = `${path}/${params}`;

  const response = await session.get<ProductBatch[]>(url);

  return response.data;
};

/**
 * Get all inventory items
 * If optional productId is passed in, get all inventory items specific to a product
 * If optional includeBatches is passed in, fetch batch information for inventory items
 * @param payload
 * @param payload.productId - id of the product to fetch its inventory items
 * @param payload.includeBatches - flag to additionally fetch batch information
 * @returns formatted inventory items
 */
export const getInventoryItems = async (payload: {
  productId?: Product['id'];
  includeBatches?: boolean;
  facilityId: InventoryFacility['id'];
}) => {
  const { includeBatches, productId, facilityId } = payload;

  const response = await apiSlugSession.get<PaginatedResponse<InventoryItem>>(`${URLS.INVENTORY.ITEMS}/`, {
    params: {
      facility: facilityId,
      ...(productId && { product: productId }),
      ...(includeBatches && { include_children: 'batch' }),
    },
  });

  const inventoryItems = response.data.results;

  if (includeBatches) {
    return formatBatchInventoryItemOptions(inventoryItems);
  }

  return inventoryItems;
};

/**
 * Creates an inventory item
 * @param item - object to be created
 * @returns newly created inventory item
 */
export const createInventoryItem = async (item: Partial<InventoryItem<number>>) => {
  const url = `${URLS.INVENTORY.ITEMS}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.post<InventoryItem<Batch>>(url, item);

  return response.data;
};

/**
 * Updates an Inventory Item
 * @param {Partial<InventoryItem>} item - inventory item to update
 * @returns updated inventory item
 */
export const updateInventoryItem = async (item: Partial<InventoryItem<number>>) => {
  const path = buildPath(URLS.INVENTORY.ITEM, { itemId: String(item.id) });
  const url = `${path}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.patch<InventoryItem<Batch>>(url, item);

  return response.data;
};

/**
 * Removes an Inventory Item
 * @param id - item id to remove
 */
export const deleteInventoryItem = (id: InventoryItem['id']) => {
  const path = buildPath(URLS.INVENTORY.ITEM, { itemId: id });
  const url = `${path}/?${URLS.CUSTOM_ERROR}`;

  return apiSlugSession.delete(url);
};

/**
 * Get Inventory Facilities
 * @param params
 * @param params.isBrandOwner - is user BME
 * @param params.ordering - what field to order facilities by (e.g. created_on)
 * @param params.zipcode - buyer zipcode, for fetching facilities with distro zones that match the selected buyer
 * @returns paginated facilities
 */
export interface GetFacilitiesParams {
  isBrandOwner?: boolean;
  limit?: number;
  ordering?: string;
  zipcode?: Customer['zipcode'];
}

export const getFacilities = async (params: GetFacilitiesParams = {}) => {
  const { isBrandOwner, zipcode, ...queryParams } = params;

  const includeDistributorsParam = isBrandOwner ? '&include_distributors=true' : '';
  const zipcodeParam = zipcode ? `&zipcode=${zipcode}` : '';
  const url = `${URLS.INVENTORY.FACILITIES}/?${URLS.CUSTOM_ERROR}${includeDistributorsParam}${zipcodeParam}`;

  const response = await apiSlugSession.get<PaginatedResponse<InventoryFacility>>(url, { params: queryParams });

  response.data.results = formatFacilities(response.data.results);

  return response.data;
};

/**
 * Creates a new Inventory Facility
 * @param facility - object to be created
 * @returns newly created facility
 */
export const createFacility = async (facility: Partial<InventoryFacility>) => {
  const url = `${URLS.INVENTORY.FACILITIES}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.post<InventoryFacility>(url, facility);

  return response.data;
};

/**
 * Updates an Inventory Facility
 * @param facility - object to be updated
 * @returns edited facility
 */
// eslint-disable-next-line require-await
export const editFacility = async (facility: Partial<InventoryFacility>) => {
  const url = `${URLS.INVENTORY.FACILITIES}/${facility.id}/?${URLS.CUSTOM_ERROR}`;

  return apiSlugSession.patch<InventoryFacility>(url, facility).then((response) => response.data);
};

/**
 * post the products csv file
 * @param formData
 * @param formData.csv_file - is the products file uploaded by the user
 * @param formData.company_id - company id selected from the company picker

 * @return Promise
 */
export const uploadCompanyProductsFile = async (formData: FormData) => {
  const url = `/api/ll/ingestion/?${URLS.CUSTOM_ERROR}`;

  const response = await createSession('', { shouldTransform: false }).post(url, formData);

  return response.data;
};

/**
 * Creates a new Batch
 * @param batch - object to be created
 * @returns newly created batch
 */
// eslint-disable-next-line require-await
export const createBatch = async (batch: Partial<Batch>) => {
  const url = `${URLS.INVENTORY.BATCHES}/?${URLS.CUSTOM_ERROR}`;

  return session.post<Batch>(url, batch).then((response) => response.data);
};

export const uploadBatchDocument = async (payload: { batchId: Batch['id']; file: File }) => {
  const formSession = createSession(URLS.INTERNAL, { shouldTransform: false });
  const url = `${URLS.INVENTORY.BATCH_DOCUMENTS}/?${URLS.CUSTOM_ERROR}`;
  const headers = { 'Content-Type': 'multipart/form-data' };

  const formData = new FormData();

  formData.append('document', payload.file);
  formData.append('batch', payload.batchId.toString());

  const response = await formSession.post<BatchDocument>(url, formData, { headers });

  return response.data;
};

/**
 * Fetch the defined strain classifications
 * @returns StrainClassification[]
 */
export const getStrainClassifications = async () => {
  const response = await session.get<StrainClassification[]>(`${URLS.INVENTORY.STRAIN_CLASSIFICATIONS}/`);
  return response.data;
};

/**
 * Fetch the defined strains
 * @returns Strain[]
 */
export const getStrains = async () => {
  const response = await session.get<Strain>(`${URLS.INVENTORY.COMPANY_STRAINS}/`);
  return response;
};

/**
 * Add a strain to a specified company
 * @param payload Strain
 * @returns Strain
 */
export const addStrain = async (payload: Partial<Strain>) => {
  const response = await session.post<Strain>(`${URLS.INVENTORY.COMPANY_STRAINS}/`, payload);
  return response;
};

/**
 * Fetch a paginated list of the available product lines
 */
export const getInventoryProductLines = async (params: ProductLineApiProductLinesListRequest) => {
  const api = await getUnifiedApi(ProductLineApiFactory);
  const response = await api.productLinesList(params);

  return response.data;
};

export const getInventoryProductLine = async (id: number) => {
  const api = await getUnifiedApi(ProductLineApiFactory);
  const response = await api.productLinesRetrieve({ id });

  return response.data;
};

/**
 * Add a product line to the inventory.
 */
export const addInventoryProductLine = async (params: ProductLineApiProductLinesCreateRequest) => {
  const api = await getUnifiedApi(ProductLineApiFactory);
  const response = await api.productLinesCreate(params);

  return response.data;
};

/**
 * Removes a product line from the inventory.
 */
export const removeInventoryProductLine = async (params: ProductLineApiProductLinesDestroyRequest) => {
  const api = await getUnifiedApi(ProductLineApiFactory);
  await api.productLinesDestroy(params);
};

/**
 * Updates a product line from the inventory.
 */
export const updateInventoryProductLine = async (params: ProductLineApiProductLinesPartialUpdateRequest) => {
  const api = await getUnifiedApi(ProductLineApiFactory);
  const response = await api.productLinesPartialUpdate(params);

  return response.data;
};

/**
 * Fetch a paginated list of the available product lines
 */
export const getUnitOfMeasures = async (params?: UnitOfMeasureApiUnitsOfMeasureListRequest) => {
  const api = await getUnifiedApi(UnitOfMeasureApiFactory);
  const response = await api.unitsOfMeasureList(params);

  return response.data;
};

/**
 * Fetch a list of available unit denominations
 */
export const getUnitDenominations = async (params?: UnitDenominationApiUnitDenominationsListRequest) => {
  const api = await getUnifiedApi(UnitDenominationApiFactory);
  const response = await api.unitDenominationsList(params);

  return response.data;
};

/**
 * List multiple product templates.
 */
export async function getProductTemplateList(params: ProductTemplateApiProductTemplatesListRequest) {
  const api = await getUnifiedApi(ProductTemplateApiFactory);
  const response = await api.productTemplatesList(params);

  return response.data;
}

/**
 * Retrieve a single product template.
 */
export async function getProductTemplate(params: ProductTemplateApiProductTemplatesRetrieveRequest) {
  const api = await getUnifiedApi(ProductTemplateApiFactory);
  const response = await api.productTemplatesRetrieve(params);

  return response.data;
}

/**
 * Creates a Product Template
 */
export async function addProductTemplate(template: UnifiedProductTemplateRequest) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(ProductTemplateApiFactory);
  const response = await api.productTemplatesCreate({
    companyId: +activeCompanyId.value,
    unifiedProductTemplateRequest: template,
  });

  return response.data;
}

export interface UpdateProductTemplateArgs {
  id: string;
  template: PatchedUnifiedProductTemplateRequest;
}
/**
 * Update a Product Template
 */
export async function updateProductTemplate({ id, template }: UpdateProductTemplateArgs) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(ProductTemplateApiFactory);
  const response = await api.productTemplatesPartialUpdate({
    patchedUnifiedProductTemplateRequest: template,
    companyId: +activeCompanyId.value,
    id,
  });

  return response.data;
}

/**
 * Deletes a Product Template
 */
export async function deleteProductTemplate(id: string) {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(ProductTemplateApiFactory);
  return await api.productTemplatesDestroy({
    id,
    companyId: +activeCompanyId.value,
  });
}

/**
 * Retrieves the available growing operation types from API, used for product category.
 */
export async function getGrowingTypeOperation() {
  const api = await getUnifiedApi(ProductGrowOperationTypeApiFactory);
  const response = await api.productGrowOperationTypesList();

  return response.data;
}

/*
 * Fetches available strains by the user active company
 */
export const getStrainsList = async (params: StrainApiStrainsListRequest) => {
  const { activeCompanyId } = storeToRefs(useCompanyStore());

  const api = await getUnifiedApi(StrainApiFactory);
  const response = await api.strainsList({
    company: [Number(activeCompanyId.value)],
    ...params,
  });

  return response.data;
};

/**
 * Fetches available strain classifications
 */
export const getStrainsClassification = async () => {
  const api = await getUnifiedApi(StrainApiFactory);
  const response = await api.strainClassificationsList();

  return response.data;
};

/**
 * Fetches available product listing status
 */
export const getProductListingStates = async () => {
  const api = await getUnifiedApi(ProductListingStateApiFactory);
  const response = await api.productListingStatesList();

  return response.data;
};

/**
 * Fetch a paginated list of the available products
 */
export const getProductsMVPList = async (params: ProductApiProductsListRequest) => {
  const api = await getUnifiedApi(ProductMVPApiFactory);
  const response = await api.productsMvpList(params);

  return response.data;
};

/**
 * Fetch a product, using the unified API
 */
export const getProductMVP = async (data: ProductMVPApiProductsMvpRetrieveRequest) => {
  const api = await getUnifiedApi(ProductMVPApiFactory);
  const response = await api.productsMvpRetrieve(data);

  return response.data;
};

/**
 * Fetch product data for cloning
 */
export const getProductMVPClone = async (data: ProductMVPApiProductsMvpCloneRetrieveRequest) => {
  const api = await getUnifiedApi(ProductMVPApiFactory);
  const response = await api.productsMvpCloneRetrieve(data);

  return response.data;
};

/**
 * Create a new product as a clone of an existing one
 */
export const createProductMVPClone = async (data: ProductMVPApiProductsMvpCloneCreateRequest) => {
  const api = await getUnifiedApi(ProductMVPApiFactory);
  const response = await api.productsMvpCloneCreate(data);

  return response.data;
};

/**
 * Create a new product in the unified API
 */
export const createProductMVP = async (data: ProductMVPApiProductsMvpCreateRequest) => {
  const api = await getUnifiedApi(ProductMVPApiFactory);
  const response = await api.productsMvpCreate(data);

  return response.data;
};

/**
 * Retrieves template data for a new product
 */
export const getProductMVPTemplate = async (id: number) => {
  const api = await getUnifiedApi(ProductMVPApiFactory);
  const response = await api.productsMvpAddFromTemplateRetrieve({ templateId: id.toString() });

  return response.data;
};

/**
 * Creates a product using a template
 */
export const createProductMVPTemplate = async (data: ProductMVPApiProductsMvpAddFromTemplateCreateRequest) => {
  const api = await getUnifiedApi(ProductMVPApiFactory);
  const response = await api.productsMvpAddFromTemplateCreate(data);

  return response.data;
};

/**
 * Fetch all product images of a product
 */
export const getProductImageList = async (data: ProductImageApiProductImagesListRequest) => {
  const api = await getUnifiedApi(ProductImageApiFactory);
  const response = await api.productImagesList(data);

  return response.data;
};

/**
 * Create a single product image
 */
export const createProductImage = async (data: ProductImageApiProductImagesCreateRequest) => {
  const api = await getUnifiedApi(ProductImageApiFactory);
  const response = await api.productImagesCreate(data);

  return response.data;
};

/**
 * Remove a single product image
 */
export const removeProductImage = async (data: ProductImageApiProductImagesDestroyRequest) => {
  const api = await getUnifiedApi(ProductImageApiFactory);
  const response = await api.productImagesDestroy(data);

  return response.data;
};

/**
 * Clone a single product image
 */
export const cloneProductImage = async (data: ProductImageApiProductImagesCloneCreateRequest) => {
  const api = await getUnifiedApi(ProductImageApiFactory);
  const response = await api.productImagesCloneCreate(data);

  return response.data;
};

/**
 * Bulk create product images in the unified API
 */
export const bulkCreateProductImages = async (data: ProductImageApiProductImagesBulkCreateCreateRequest) => {
  const api = await getUnifiedApi(ProductImageApiFactory);
  const response = await api.productImagesBulkCreateCreate(data);

  return response.data;
};

/**
 * Bulk price update
 */
export const bulkPriceUpdate = async (data: UnifiedProductBulkUpdateRequest[]) => {
  const api = await getUnifiedApi(ProductApiFactory);
  const response = await api.productsBulkUpdate({ unifiedProductBulkUpdateRequest: data });

  return response.data;
};

/**
 * Fetch a list of available product specifications
 */
export const getProductSpecifications = async (params?: ProductSpecificationApiProductSpecificationsListRequest) => {
  const api = await getUnifiedApi(ProductSpecificationApiFactory);
  const response = await api.productSpecificationsList(params);

  return response.data;
};

/**
 * Fetch a list of available product dosage units
 */
export const getProductDosageUnits = async (params?: ProductDosageUnitApiProductDosageUnitsListRequest) => {
  const api = await getUnifiedApi(ProductDosageUnitApiFactory);
  const response = await api.productDosageUnitsList(params);

  return response.data;
};
